import { RVUPeriod, ShiftDifferentialType } from '~/types/hlx/enums';

export type MoneyCalculationDetail = {
  id?: number | null;
  pricePerHour?: number | string | null;
  pricePerRvu?: number | string | null;
  totalContractValue?: number | string | null;
  hoursPerShift?: number | string | null;
  minRvusPerShift?: number | null;
  numOfShifts?: number | null;
  rvusPerPeriod?: number | null;
  rvuPeriod?: RVUPeriod | null;
  yearlyRvusPerFte?: number | null;
  fteShiftsPerYear?: number | null;
};

const isFiniteMoneyValue = (value: number | string | null | undefined): boolean =>
  value != null &&
  (typeof value !== 'string' || value.trim() !== '') &&
  Number.isFinite(Number(value));

const hasPersistedDetailId = (id: number | null | undefined): boolean =>
  Number.isInteger(id) && Number(id) > 0;

export const hasValidCostCalculation = (
  detail: MoneyCalculationDetail | null | undefined
): boolean =>
  detail != null &&
  hasPersistedDetailId(detail.id) &&
  isFiniteMoneyValue(detail.totalContractValue) &&
  isFiniteMoneyValue(detail.pricePerHour);

export const formatCalculatedMoney = (value: number | string | null | undefined): string =>
  isFiniteMoneyValue(value)
    ? `$${Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : 'Unavailable';

export const calculateRvusPerShift = (detail: MoneyCalculationDetail): number => {
  const rvusPerPeriod = Number(detail.rvusPerPeriod ?? 0);
  if (Number.isFinite(rvusPerPeriod) && rvusPerPeriod > 0) {
    switch (detail.rvuPeriod) {
      case RVUPeriod.SHIFT:
        return rvusPerPeriod;
      case RVUPeriod.HOUR: {
        const hoursPerShift = Number(detail.hoursPerShift ?? 0);
        return hoursPerShift > 0 ? rvusPerPeriod * hoursPerShift : 0;
      }
      case RVUPeriod.YEAR: {
        const shiftCount = Number(detail.numOfShifts ?? detail.fteShiftsPerYear ?? 0);
        return shiftCount > 0 ? rvusPerPeriod / shiftCount : 0;
      }
    }
  }

  const minimumRvusPerShift = Number(detail.minRvusPerShift ?? 0);
  if (Number.isFinite(minimumRvusPerShift) && minimumRvusPerShift > 0) {
    return minimumRvusPerShift;
  }

  const yearlyRvusPerFte = Number(detail.yearlyRvusPerFte ?? 0);
  const fteShiftsPerYear = Number(detail.fteShiftsPerYear ?? 0);
  if (
    Number.isFinite(yearlyRvusPerFte) &&
    yearlyRvusPerFte > 0 &&
    Number.isFinite(fteShiftsPerYear) &&
    fteShiftsPerYear > 0
  ) {
    return yearlyRvusPerFte / fteShiftsPerYear;
  }

  return 0;
};

export const resolveDiffType = (
  diffType: ShiftDifferentialType | null | undefined
): ShiftDifferentialType => diffType ?? ShiftDifferentialType.INCR_HOURLY_RATE;

/*
 * Differential branches:
 *   fixed hourly -> the differential replaces the base rate
 *   RVU          -> RVUs/shift x price/RVU / hours/shift
 *   multiplier   -> base rate x multiplier
 *   increment    -> base rate + hourly increment
 * Missing optional shift inputs resolve to zero for presentation rows. The
 * calculator components separately enforce the required money invariants.
 */
export const calculateCostPerHour = (
  detail: MoneyCalculationDetail,
  differential: number,
  diffType?: ShiftDifferentialType | null
): number => {
  const resolvedDiffType = resolveDiffType(diffType);
  const pricePerHour = Number(detail.pricePerHour ?? 0);

  // The base row always uses the persisted base rate. RVU metadata is needed
  // to price an RVU differential, but its absence must not erase a valid base.
  if (differential === 0) return pricePerHour;

  switch (resolvedDiffType) {
    case ShiftDifferentialType.HOURLY_RATE:
      return differential;
    case ShiftDifferentialType.RVU: {
      const hoursPerShift = Number(detail.hoursPerShift ?? 0);
      const rvusPerShift = calculateRvusPerShift(detail);
      if (hoursPerShift === 0 || rvusPerShift === 0) return 0;
      return (rvusPerShift * differential) / hoursPerShift;
    }
    case ShiftDifferentialType.MULTIPLIER:
      return pricePerHour * differential;
    case ShiftDifferentialType.INCR_HOURLY_RATE:
      return pricePerHour + differential;
    default: {
      const exhaustive: never = resolvedDiffType;
      throw new Error(`Invalid shift differential type: ${exhaustive}`);
    }
  }
};

export const calculateCostPerShift = (
  detail: MoneyCalculationDetail,
  costPerHour: number
): number => Number(detail.hoursPerShift ?? 0) * costPerHour;

export const calculatePricePerRVU = (
  detail: MoneyCalculationDetail,
  differential: number,
  diffType?: ShiftDifferentialType | null,
  hourlyRate?: number
): number => {
  const rvusPerShift = calculateRvusPerShift(detail);
  if (rvusPerShift === 0) return 0;

  const resolvedDiffType = resolveDiffType(diffType);
  if (resolvedDiffType === ShiftDifferentialType.RVU) {
    return differential === 0 ? Number(detail.pricePerRvu ?? 0) : differential;
  }

  const resolvedHourlyRate =
    hourlyRate ?? calculateCostPerHour(detail, differential, resolvedDiffType);
  return (Number(detail.hoursPerShift ?? 0) * resolvedHourlyRate) / rvusPerShift;
};
